import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ImpersonationRepository } from '../impersonation.repository';
import { ImpersonationService } from '../impersonation.service';
import { SearchCriteria, SearchResult } from '../models/search-result.model';
import * as ImpersonationActions from './impersonation.actions';
import { UserImpersonationAuth } from '../models/user-impersonation-auth.model';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ImpersonationStopReason, IMPERSONATION_CONSTANTS } from '../impersonation.constants';
import { AppKey, AppUrlService } from '@capital-access/common/settings';
import { selectTargetUserId, selectOriginalUserId, selectIsConsultantImpersonation } from './impersonation.selectors';
import { userAuthenticated } from '@capital-access/common/actions';
import { JsonPatchDocument } from '../models/json-patch-operation.model';
import { UserRepository } from '../../user/user.repository';
import { ImpersonationAuditActionType } from '../models/impersonation-action-log.model';
import { Log } from '@capital-access/common/logging';

type ImpersonationValidationResult =
  | ReturnType<typeof ImpersonationActions.setImpersonationSuccess>
  | ReturnType<typeof ImpersonationActions.setImpersonationFailed>;

@Injectable()
export class ImpersonationEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private impersonationRepository = inject(ImpersonationRepository);
  private impersonationService = inject(ImpersonationService);
  private oidcSecurityService = inject(OidcSecurityService);
  private appUrlService = inject(AppUrlService);
  private userRepository = inject(UserRepository);

  initializeImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.initializeImpersonation),
      filter(({ targetUserId }) => !!targetUserId),
      switchMap(({ targetUserId, isConsultantImpersonation }) =>
        isConsultantImpersonation
          ? this.validateConsultantUserImpersonation(targetUserId)
          : this.validateUserImpersonation(targetUserId)
      )
    )
  );

  handleStopImpersonation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImpersonationActions.stopImpersonation),
        tap(({ reason, isConsultantImpersonation }) =>
          this.handleImpersonationStop(reason!, !!isConsultantImpersonation)
        )
      ),
    { dispatch: false }
  );

  recordImpersonationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.setImpersonationSuccess),
      withLatestFrom(this.store.select(selectTargetUserId), this.store.select(selectOriginalUserId)),
      filter(([, targetUserId, originalUserId]) => this.shouldRecordHistory(targetUserId, originalUserId)),
      switchMap(([{ userImpersonationAuthId }, targetUserId, originalUserId]) =>
        this.recordHistory(userImpersonationAuthId, targetUserId!, originalUserId!)
      )
    )
  );

  loadUserProfileAfterImpersonationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.setImpersonationSuccess),
      map(() => userAuthenticated({ isInitializingImpersonation: false }))
    )
  );

  monitorImpersonationExpiration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.setImpersonationSuccess),
      filter(({ accessExpiresDateTime }) => !!accessExpiresDateTime),
      switchMap(({ accessExpiresDateTime }) => this.createExpirationTimer(accessExpiresDateTime!))
    )
  );

  handleImpersonationFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.setImpersonationFailed),
      withLatestFrom(this.store.select(selectIsConsultantImpersonation)),
      map(([{ reason }, isConsultantImpersonation]) =>
        ImpersonationActions.stopImpersonation({ reason, isConsultantImpersonation })
      )
    )
  );

  logImpersonationAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImpersonationActions.logImpersonationAction),
        mergeMap(({ request }) =>
          this.impersonationRepository.logImpersonationAction(request).pipe(catchError(() => EMPTY))
        )
      ),
    { dispatch: false }
  );

  trackImpersonationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.trackImpersonationRequest),
      withLatestFrom(this.store.select(selectTargetUserId), this.store.select(selectOriginalUserId)),
      filter(([, targetUserId, originalUserId]) => !!targetUserId && !!originalUserId),
      map(([{ method, url, body }, targetUserId, originalUserId]) => {
        const HTTP_METHOD_TO_ACTION_TYPE: Record<string, ImpersonationAuditActionType> = {
          POST: ImpersonationAuditActionType.CREATE,
          PUT: ImpersonationAuditActionType.UPDATE,
          PATCH: ImpersonationAuditActionType.UPDATE,
          DELETE: ImpersonationAuditActionType.DELETE
        };

        const actionType = HTTP_METHOD_TO_ACTION_TYPE[method.toUpperCase()];

        return ImpersonationActions.logImpersonationAction({
          request: {
            userId: targetUserId!,
            modifiedByUserId: originalUserId!,
            entity: body || {},
            actionType,
            apiPath: url
          }
        });
      })
    )
  );

  private handleImpersonationStop(reason: ImpersonationStopReason, isConsultantImpersonation: boolean): void {
    const historyId = Number(localStorage.getItem(IMPERSONATION_CONSTANTS.IMPERSONATION_HISTORY_ID));

    if (historyId && this.shouldUpdateImpersonationEndDate(reason)) {
      this.updateEndDateBeforeLogout(historyId, reason, isConsultantImpersonation);
    } else {
      this.performLogout(reason, isConsultantImpersonation);
    }
  }

  private shouldUpdateImpersonationEndDate(reason: ImpersonationStopReason): boolean {
    return (
      reason === ImpersonationStopReason.Logout ||
      reason === ImpersonationStopReason.UserInitiated ||
      reason === ImpersonationStopReason.Expired
    );
  }

  private updateEndDateBeforeLogout(
    historyId: number,
    reason: ImpersonationStopReason,
    isConsultantImpersonation: boolean
  ): void {
    const patchRequest: JsonPatchDocument = [
      {
        op: 'replace',
        path: IMPERSONATION_CONSTANTS.IMPERSONATION_HISTORY_END_DATE_PATH,
        value: new Date().toISOString()
      }
    ];

    this.impersonationRepository
      .updateImpersonationEndDate(historyId, patchRequest)
      .pipe(
        catchError(() => {
          // Silent error handling - don't let API failure prevent logout
          return EMPTY;
        })
      )
      .subscribe({
        complete: () => this.performLogout(reason, isConsultantImpersonation)
      });
  }

  private performLogout(reason: ImpersonationStopReason, isConsultantImpersonation: boolean): void {
    this.impersonationService.clearLocalStorageData();

    switch (reason) {
      case ImpersonationStopReason.ValidationFailed:
      case ImpersonationStopReason.Logout:
        this.oidcSecurityService
          .logoff()
          .pipe(
            take(1),
            catchError(() => EMPTY)
          )
          .subscribe();
        break;
      case ImpersonationStopReason.UserInitiated:
      case ImpersonationStopReason.Expired:
      case ImpersonationStopReason.ApiError:
        try {
          this.oidcSecurityService.logoffLocal();
        } catch (error) {
          // Silent error handling - OIDC state errors during expired sessions are expected
          Log.warn(`OIDC state errors during expired sessions are expected: ${error}`);
        }

        // Navigate based on impersonation type
        if (isConsultantImpersonation) {
          this.impersonationService.navigateBackToCapitalAccess();
        } else {
          this.impersonationService.navigateBackToAdminPortal();
        }
        break;
    }
  }

  private validateUserImpersonation(targetUserId: number): Observable<ImpersonationValidationResult> {
    const validationError = this.getValidationError(targetUserId);
    if (validationError) {
      return of(validationError);
    }

    return this.impersonationRepository.getUsers(this.createSearchCriteria(targetUserId), false).pipe(
      switchMap((result: SearchResult<UserImpersonationAuth>) => this.processUserValidation(result)),
      catchError(() => of(this.createFailedAction(ImpersonationStopReason.ApiError)))
    );
  }

  private validateConsultantUserImpersonation(targetUserId: number): Observable<ImpersonationValidationResult> {
    const validationError = this.getValidationError(targetUserId);
    if (validationError) {
      return of(validationError);
    }

    return this.impersonationRepository
      .getConsultantImpersonationUsers(this.createSearchCriteria(targetUserId), false)
      .pipe(
        switchMap((result: SearchResult<UserImpersonationAuth>) => this.processUserValidation(result)),
        catchError(() => of(this.createFailedAction(ImpersonationStopReason.ApiError)))
      );
  }

  private getValidationError(targetUserId: number): ImpersonationValidationResult | null {
    if (!targetUserId || targetUserId <= 0) {
      return this.createFailedAction(ImpersonationStopReason.ValidationFailed);
    }

    const capitalAccessUrl = this.appUrlService.getAppUrl(AppKey.CAPITAL_ACCESS);
    const currentUrl = `${window.location.origin}${window.location.pathname}`;

    if (!currentUrl.startsWith(capitalAccessUrl)) {
      return this.createFailedAction(ImpersonationStopReason.ValidationFailed);
    }

    return null;
  }

  private createSearchCriteria(targetUserId: number): SearchCriteria {
    return {
      key: targetUserId.toString(),
      skip: 0,
      take: 1
    };
  }

  private processUserValidation(
    result: SearchResult<UserImpersonationAuth>
  ): Observable<ImpersonationValidationResult> {
    const authDetails = result.result?.[0];

    if (!authDetails?.isActive) {
      return of(this.createFailedAction(ImpersonationStopReason.ValidationFailed));
    }

    return this.userRepository.getUserProfile().pipe(
      map(userProfile =>
        ImpersonationActions.setImpersonationSuccess({
          accessExpiresDateTime: authDetails.accessExpiresDateTime,
          accessScope: authDetails.accessScope,
          userImpersonationAuthId: authDetails.id,
          originalUserId: userProfile.user.id
        })
      ),
      catchError(() => of(this.createFailedAction(ImpersonationStopReason.ApiError)))
    );
  }

  private createFailedAction(
    reason: ImpersonationStopReason
  ): ReturnType<typeof ImpersonationActions.setImpersonationFailed> {
    return ImpersonationActions.setImpersonationFailed({ reason });
  }

  private shouldRecordHistory(targetUserId?: number | null, originalUserId?: number | null): boolean {
    const hasValidUserIds = !!(targetUserId && originalUserId);
    const isHistoryNotRecorded = !localStorage.getItem(IMPERSONATION_CONSTANTS.IMPERSONATION_HISTORY_ID);
    return hasValidUserIds && isHistoryNotRecorded;
  }

  private recordHistory(
    userImpersonationAuthId: number,
    targetUserId: number,
    originalUserId: number
  ): Observable<any> {
    return this.impersonationRepository
      .recordImpersonationHistory({
        userId: originalUserId,
        impersonatedUserId: targetUserId,
        impersonationStartDateTime: new Date().toISOString(),
        userImpersonationAuthId
      })
      .pipe(
        tap((historyId: number) => {
          localStorage.setItem(IMPERSONATION_CONSTANTS.IMPERSONATION_HISTORY_ID, historyId.toString());
        }),
        switchMap(() => EMPTY),
        catchError(() => of(this.createFailedAction(ImpersonationStopReason.ApiError)))
      );
  }

  private createExpirationTimer(
    accessExpiresDateTime: string
  ): Observable<ReturnType<typeof ImpersonationActions.stopImpersonation>> {
    const MAX_TIMER_VALUE = 2147483647;
    let timeUntilExpiry = new Date(accessExpiresDateTime).getTime() - Date.now();

    if (timeUntilExpiry <= 0) {
      return this.store.select(selectIsConsultantImpersonation).pipe(
        take(1),
        map(isConsultantImpersonation =>
          ImpersonationActions.stopImpersonation({
            reason: ImpersonationStopReason.Expired,
            isConsultantImpersonation
          })
        )
      );
    }

    // Cap timer to JavaScript's max setTimeout value (2^31-1 ms) to prevent immediate firing
    if (timeUntilExpiry > MAX_TIMER_VALUE) {
      timeUntilExpiry = MAX_TIMER_VALUE - 1;
    }

    return timer(timeUntilExpiry).pipe(
      withLatestFrom(this.store.select(selectIsConsultantImpersonation)),
      map(([, isConsultantImpersonation]) =>
        ImpersonationActions.stopImpersonation({
          reason: ImpersonationStopReason.Expired,
          isConsultantImpersonation
        })
      ),
      takeUntil(this.actions$.pipe(ofType(ImpersonationActions.stopImpersonation)))
    );
  }
}
